(() => {
  const root = document.querySelector("[data-spoils-new-look-homepage]");
  if (!root) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const motionAllowed = window.matchMedia("(prefers-reduced-motion: no-preference)");
  const desktopMotion = window.matchMedia("(min-width: 901px)");
  const heroVideo = root.querySelector("[data-snl-hero-video]");
  const heroVideoSources = root.querySelector("[data-snl-hero-video-sources]");
  const heroFallback = root.querySelector("[data-snl-hero-fallback]");
  const scrollAct = root.querySelector("[data-scroll-act]");
  const mobileFinal = root.querySelector("[data-mobile-scroll-act]");
  const beats = scrollAct ? Array.from(scrollAct.querySelectorAll("[data-beat]")) : [];
  const mobileBeats = mobileFinal ? Array.from(mobileFinal.querySelectorAll("[data-mobile-beat]")) : [];
  const beatWindows = [
    { start: 0, end: 0.25 },
    { start: 0.34, end: 0.53 },
    { start: 0.62, end: 0.76 }
  ];
  const claimedCarousel = root.querySelector(".snl-claimed .snl-residents--teaser");
  let heroVideoHydrated = false;

  function hydrateHeroVideo() {
    if (!heroVideo || !heroVideoSources || heroVideoHydrated) return;

    const poster = heroVideo.dataset.snlHeroPoster;
    if (poster) heroVideo.setAttribute("poster", poster);
    heroVideo.appendChild(heroVideoSources.content.cloneNode(true));
    heroVideoHydrated = true;
  }

  function showHeroFallback() {
    if (heroVideo) {
      heroVideo.pause();
      heroVideo.hidden = true;
      heroVideo.removeAttribute("autoplay");
    }

    if (heroFallback) heroFallback.hidden = false;
  }

  function playHeroVideoWhenReady() {
    if (!heroVideo) return;

    heroVideo.hidden = false;
    const playAttempt = heroVideo.play();
    if (playAttempt && typeof playAttempt.then === "function") {
      playAttempt
        .then(() => {
          if (heroFallback) heroFallback.hidden = true;
        })
        .catch(showHeroFallback);
      return;
    }

    if (heroFallback) heroFallback.hidden = true;
  }

  function startHeroVideo() {
    if (!heroVideo || !heroFallback) return;

    if (reduceMotion.matches || !desktopMotion.matches) {
      showHeroFallback();
      return;
    }

    hydrateHeroVideo();
    heroVideo.hidden = false;
    heroVideo.muted = true;
    heroVideo.loop = true;
    heroVideo.playsInline = true;
    heroVideo.preload = "metadata";
    heroVideo.setAttribute("autoplay", "");

    // On desktop, let the video element show its current poster while media
    // buffers. The image fallback is legacy photography and should only appear
    // if playback actually fails (or reduced motion requests it).
    heroFallback.hidden = desktopMotion.matches;

    if (heroVideo.readyState >= 2) {
      playHeroVideoWhenReady();
      return;
    }

    heroVideo.hidden = false;
    heroVideo.addEventListener("loadeddata", playHeroVideoWhenReady, { once: true });
    heroVideo.load();
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function clearScrollActVariables() {
    if (!scrollAct) return;
    scrollAct.style.removeProperty("--snl-scroll-beat-opacity");
    scrollAct.style.removeProperty("--snl-scroll-reveal-opacity");
    scrollAct.style.removeProperty("--snl-scroll-bed-y");
    scrollAct.style.removeProperty("--snl-scroll-title-y");
    scrollAct.style.removeProperty("--snl-scroll-close-y");
    scrollAct.style.removeProperty("--snl-scroll-close-opacity");
    beats.forEach((beat) => {
      beat.classList.remove("is-active");
      beat.setAttribute("aria-hidden", "true");
    });
  }

  function activeBeatIndex(progress) {
    return beatWindows.findIndex((window) => progress >= window.start && progress < window.end);
  }

  function progressBetween(progress, start, end) {
    return clamp((progress - start) / Math.max(0.001, end - start), 0, 1);
  }

  function updateScrollEnhancement() {
    if (!scrollAct) return;

    const canEnhance = desktopMotion.matches && motionAllowed.matches && !reduceMotion.matches;
    scrollAct.classList.toggle("is-scroll-enhanced", canEnhance);

    if (!canEnhance) {
      scrollAct.classList.remove("is-reveal");
      clearScrollActVariables();
    }
  }

  function updateScrollAct() {
    if (!scrollAct || !scrollAct.classList.contains("is-scroll-enhanced")) return;

    const rect = scrollAct.getBoundingClientRect();
    const travel = Math.max(1, rect.height - window.innerHeight);
    const progress = clamp(-rect.top / travel, 0, 1);
    const activeBeat = activeBeatIndex(progress);
    const revealOpacity = progressBetween(progress, 0.78, 0.86);
    const revealProgress = progressBetween(progress, 0.84, 1);
    const titleProgress = progressBetween(progress, 0.76, 0.94);
    const sticky = scrollAct.querySelector(".snl-scroll-act__sticky");
    const reveal = scrollAct.querySelector(".snl-scroll-act__reveal");
    const bed = scrollAct.querySelector(".snl-scroll-act__bed");
    const title = scrollAct.querySelector(".snl-scroll-act__reveal h2");
    const close = scrollAct.querySelector(".snl-scroll-act__close");
    const stickyBounds = sticky?.getBoundingClientRect();
    const revealBounds = reveal?.getBoundingClientRect();
    const bedBounds = bed?.getBoundingClientRect();
    const bedHeight = bedBounds?.height || 0;
    const bedLift = parseFloat(getComputedStyle(scrollAct).getPropertyValue("--snl-scroll-bed-lift")) || 124;
    const bedShift = (1 - revealProgress) * 0.54 * bedHeight + bedLift;
    const priorBedShift = parseFloat(scrollAct.style.getPropertyValue("--snl-scroll-bed-y")) || bedHeight * 0.54 + bedLift;
    const bedBaseTop = (bedBounds?.top || 0) - priorBedShift;
    const bedTop = bedBaseTop + bedShift;
    const copyClearance = parseFloat(getComputedStyle(scrollAct).getPropertyValue("--snl-scroll-copy-clearance")) || 80;

    // The transparent PNG's visible bed begins 37.6% into its canvas. Keep the
    // headline moving on its own curve until it settles behind the bed.
    const bedArtTop = bedHeight * 0.3760416667;
    // The current 1254px transparent asset's visible pixels run from roughly
    // 37.6% to 78.9% of the canvas, including the dogs and grounding shadow.
    const bedArtHeight = bedHeight * 0.413;
    const titleBaseBottom = (revealBounds?.top || 0) + (title?.offsetTop || 0) + (title?.offsetHeight || 0);
    const finalBedTop = bedBaseTop + bedLift;
    const titleOverlap = Math.min(96, Math.max(48, (title?.offsetHeight || 0) * 0.46));
    const titleTargetBottom = finalBedTop + bedArtTop + titleOverlap;
    const titleFinalShift = titleTargetBottom - titleBaseBottom;
    const titleStartShift = titleFinalShift - Math.max(72, (title?.offsetHeight || 0) * 0.58);
    const titleShift = titleStartShift + (titleFinalShift - titleStartShift) * titleProgress;
    const closeBaseTop = (stickyBounds?.top || 0) + (close?.offsetTop || 0);
    const finalBedBottom = finalBedTop + bedArtTop + bedArtHeight;
    const closeShift = Math.max(0, finalBedBottom + copyClearance - closeBaseTop);

    scrollAct.classList.toggle("is-reveal", revealOpacity > 0);

    beats.forEach((beat, index) => {
      const isActive = index === activeBeat;
      beat.classList.toggle("is-active", isActive);
      beat.setAttribute("aria-hidden", String(!isActive));
    });

    scrollAct.style.setProperty("--snl-scroll-beat-opacity", activeBeat === -1 ? "0" : "1");
    scrollAct.style.setProperty("--snl-scroll-reveal-opacity", revealOpacity.toFixed(3));
    scrollAct.style.setProperty("--snl-scroll-bed-y", `${bedShift.toFixed(2)}px`);
    scrollAct.style.setProperty("--snl-scroll-title-y", `${titleShift.toFixed(2)}px`);
    scrollAct.style.setProperty("--snl-scroll-close-y", `${closeShift.toFixed(2)}px`);
    scrollAct.style.setProperty("--snl-scroll-close-opacity", progressBetween(progress, 0.92, 0.98).toFixed(3));
  }

  function clearMobileScrollVariables() {
    if (!mobileFinal) return;
    ["--snl-mobile-beat-opacity", "--snl-mobile-reveal-opacity", "--snl-mobile-close-opacity"].forEach((property) => {
      mobileFinal.style.removeProperty(property);
    });
    mobileFinal.style.removeProperty("--snl-mobile-title-y");
    mobileFinal.classList.remove("is-mobile-scroll-enhanced");
    mobileBeats.forEach((beat, index) => {
      beat.classList.toggle("is-active", index === 0);
      beat.setAttribute("aria-hidden", String(index !== 0));
    });
  }

  function updateMobileScrollSequence() {
    if (!mobileFinal) return;

    const canEnhance = !desktopMotion.matches && motionAllowed.matches && !reduceMotion.matches;
    if (!canEnhance) {
      clearMobileScrollVariables();
      return;
    }

    mobileFinal.classList.add("is-mobile-scroll-enhanced");
    const rect = mobileFinal.getBoundingClientRect();
    const travel = Math.max(1, rect.height - window.innerHeight);
    const progress = clamp(-rect.top / travel, 0, 1);
    const activeBeat = beatWindows.findIndex((window) => progress >= window.start && progress < window.end);
    const revealOpacity = progressBetween(progress, 0.72, 0.82);
    // Let the bed finish its rise with the reveal fade; it should hold its
    // landing position while the CTA continues into view.
    const revealProgress = progressBetween(progress, 0.72, 0.82);
    const titleProgress = progressBetween(progress, 0.66, 0.92);
    // Bring the mobile CTA in with the bed reveal so it is visible in the
    // same frame as the wordmark and product, rather than appearing only
    // after the reader has already passed the reveal.
    const closeOpacity = progressBetween(progress, 0.76, 0.84);

    mobileBeats.forEach((beat, index) => {
      const isActive = index === activeBeat;
      beat.classList.toggle("is-active", isActive);
      beat.setAttribute("aria-hidden", String(!isActive));
    });

    mobileFinal.style.setProperty("--snl-mobile-beat-opacity", activeBeat === -1 ? "0" : "1");
    mobileFinal.style.setProperty("--snl-mobile-reveal-opacity", revealOpacity.toFixed(3));
    mobileFinal.style.setProperty("--snl-mobile-reveal-progress", revealProgress.toFixed(3));
    mobileFinal.style.setProperty("--snl-mobile-title-y", `${(-8 + titleProgress * 10).toFixed(2)}vh`);
    mobileFinal.style.setProperty("--snl-mobile-close-opacity", closeOpacity.toFixed(3));
  }

  function updateParallax() {
    if (reduceMotion.matches || !desktopMotion.matches) return;

    root.querySelectorAll("[data-parallax]").forEach((node) => {
      const rect = node.getBoundingClientRect();
      const centerOffset = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
      const distance = node.dataset.parallax === "slow" ? -18 : 14;
      node.style.transform = `translateY(${centerOffset * distance}px)`;
    });
  }

  function setupClaimedCarousel() {
    if (!claimedCarousel) return;

    const originals = [...claimedCarousel.querySelectorAll(":scope > .snl-residents__card--teaser")];
    if (originals.length < 2) return;

    const before = originals[originals.length - 1].cloneNode(true);
    const after = originals[0].cloneNode(true);
    [before, after].forEach((clone) => {
      clone.classList.add("is-loop-clone");
      clone.setAttribute("aria-hidden", "true");
      clone.removeAttribute("id");
      clone.removeAttribute("data-shopify-editor-block");
      clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
      clone.querySelectorAll("[data-shopify-editor-block]").forEach((node) => node.removeAttribute("data-shopify-editor-block"));
    });
    claimedCarousel.prepend(before);
    claimedCarousel.append(after);
    claimedCarousel.tabIndex = 0;
    claimedCarousel.setAttribute("role", "region");
    claimedCarousel.setAttribute("aria-label", "Claimed dogs carousel");

    const allCards = () => [...claimedCarousel.querySelectorAll(":scope > .snl-residents__card--teaser")];
    const focusX = () => {
      const bounds = claimedCarousel.getBoundingClientRect();
      return bounds.left + bounds.width / 2;
    };
    const closestCard = () => allCards().reduce((closest, card) => {
      const bounds = card.getBoundingClientRect();
      const distance = Math.abs(bounds.left + bounds.width / 2 - focusX());
      return !closest || distance < closest.distance ? { card, distance } : closest;
    }, null)?.card;
    const centerCard = (card, behavior = "auto") => {
      if (!card) return;
      const railBounds = claimedCarousel.getBoundingClientRect();
      const cardBounds = card.getBoundingClientRect();
      const delta = cardBounds.left + cardBounds.width / 2 - (railBounds.left + railBounds.width / 2);
      if (behavior === "smooth") claimedCarousel.scrollBy({ left: delta, behavior });
      else {
        claimedCarousel.classList.add("is-loop-jump");
        claimedCarousel.scrollLeft += delta;
        window.requestAnimationFrame(() => claimedCarousel.classList.remove("is-loop-jump"));
      }
    };

    function updateCards() {
      const center = focusX();
      allCards().forEach((card) => {
        const bounds = card.getBoundingClientRect();
        const offset = (bounds.left + bounds.width / 2 - center) / Math.max(bounds.width, 1);
        const distance = clamp(Math.abs(offset), 0, 1.15);
        card.style.setProperty("--claim-loop-y", `${(distance * 26).toFixed(1)}px`);
        card.style.setProperty("--claim-loop-scale", `${(1 - distance * .14).toFixed(3)}`);
        card.style.setProperty("--claim-loop-rotate", `${(-offset * 2.5).toFixed(2)}deg`);
        card.style.setProperty("--claim-loop-opacity", `${(1 - distance * .16).toFixed(3)}`);
        card.style.zIndex = String(Math.round((1.2 - distance) * 100));
      });
    }

    function wrapLoop() {
      const active = closestCard();
      if (active === before) centerCard(originals[originals.length - 1]);
      if (active === after) centerCard(originals[0]);
      updateCards();
    }

    let carouselFrame = null;
    let settleTimer = null;
    function onCarouselScroll() {
      if (!carouselFrame) {
        carouselFrame = window.requestAnimationFrame(() => {
          updateCards();
          carouselFrame = null;
        });
      }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(wrapLoop, 140);
    }

    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragging = false;
    claimedCarousel.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || event.pointerType !== "mouse") return;
      dragging = true;
      dragStartX = event.clientX;
      dragStartScroll = claimedCarousel.scrollLeft;
      claimedCarousel.classList.add("is-dragging");
      claimedCarousel.setPointerCapture(event.pointerId);
    });
    claimedCarousel.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      claimedCarousel.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
    });
    const stopDragging = () => {
      if (!dragging) return;
      dragging = false;
      claimedCarousel.classList.remove("is-dragging");
      wrapLoop();
    };
    claimedCarousel.addEventListener("pointerup", stopDragging);
    claimedCarousel.addEventListener("pointercancel", stopDragging);
    claimedCarousel.addEventListener("scroll", onCarouselScroll, { passive: true });
    claimedCarousel.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const cards = allCards();
      const active = closestCard();
      const index = cards.indexOf(active);
      centerCard(cards[index + (event.key === "ArrowRight" ? 1 : -1)], "smooth");
    });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        centerCard(originals[0]);
        updateCards();
      });
    });
  }

  let frame = null;
  function onScroll() {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      updateScrollAct();
      updateMobileScrollSequence();
      updateParallax();
      frame = null;
    });
  }

  updateScrollEnhancement();
  updateScrollAct();
  updateMobileScrollSequence();
  updateParallax();
  setupClaimedCarousel();
  startHeroVideo();
  if (heroVideo) heroVideo.addEventListener("error", showHeroFallback);

  // The reveal image and editorial font can finish loading after the first
  // scroll calculation. Re-measure when they settle so a restored scroll
  // position never leaves the desktop sequence frozen on stale geometry.
  scrollAct?.querySelectorAll("img").forEach((image) => {
    if (!image.complete) image.addEventListener("load", onScroll, { once: true });
  });
  document.fonts?.ready.then(onScroll).catch(() => {});

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("load", onScroll, { once: true });
  window.addEventListener("pageshow", () => {
    updateScrollEnhancement();
    updateScrollAct();
    updateMobileScrollSequence();
  });
  window.addEventListener("resize", () => {
    updateScrollEnhancement();
    updateMobileScrollSequence();
    onScroll();
  });
  reduceMotion.addEventListener("change", () => {
    updateScrollEnhancement();
    updateMobileScrollSequence();
    startHeroVideo();
    onScroll();
  });
  motionAllowed.addEventListener("change", () => {
    updateScrollEnhancement();
    updateMobileScrollSequence();
    onScroll();
  });
  desktopMotion.addEventListener("change", () => {
    updateScrollEnhancement();
    updateMobileScrollSequence();
    startHeroVideo();
    onScroll();
  });
})();
